import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from 'gatsby-plugin-intl';
import * as React from 'react';

import { createNotificationStorageHook } from 'hooks/notification-storage';
import { NotificationDataQuery } from 'types/codegen';

import { NOTIFICATION_VERSION_KEY } from '../constants';
import { NOTIFICATION_VARIANTS } from '../types';
import { getVariant } from '../utils';

type CurrentSuperNotificationHookResult = {
  markup: string;
  variant: NOTIFICATION_VARIANTS;
  version: string;
  homeOnly: boolean;
  active: boolean;
};

export const useCurrentNotification = (): CurrentSuperNotificationHookResult => {
  const { locale } = useIntl();

  const queryData = useStaticQuery<NotificationDataQuery>(graphql`
    query NotificationData {
      allMdx(filter: { fileAbsolutePath: { regex: "/notification/" } }) {
        nodes {
          frontmatter {
            variant
            version
            homeOnly
            active
          }
          body
          parent {
            ... on File {
              name
            }
          }
        }
      }
    }
  `);

  const result: CurrentSuperNotificationHookResult = React.useMemo(() => {
    const nodeForCurrentLanguage = queryData.allMdx.nodes.filter((node) => node.parent?.name === locale);
    const ownNode = nodeForCurrentLanguage.find(Boolean);

    const markup = ownNode?.body || '';
    const variant = getVariant(ownNode?.frontmatter?.variant);
    const version = ownNode?.frontmatter?.version || '0';
    const homeOnly = Boolean(ownNode?.frontmatter?.homeOnly);
    const active = Boolean(ownNode?.frontmatter?.active);

    return {
      markup,
      variant,
      version,
      homeOnly,
      active,
    };
  }, [queryData, locale, queryData.allMdx.nodes]);

  return result;
};

export const useSeenCurrentNotification = createNotificationStorageHook(NOTIFICATION_VERSION_KEY);
